import { Operation as LoggingOperation, Log, LogCategory } from '@augloop/logging';
import { SessionInitMessage, currentProtocolVersion, TokenProvisionMessage } from '@augloop/session-protocol';
import { Utils } from '../utils';
import { EventEmitter } from 'eventemitter3';
export class SessionInitializer extends EventEmitter {
    constructor(getSessionStats, clientMetadata, tokenRefreshManager, sendMessage, options) {
        super();
        this.getSessionStats = getSessionStats;
        this.clientMetadata = clientMetadata;
        this.tokenRefreshManager = tokenRefreshManager;
        this.sendMessage = sendMessage;
        this.options = options ? options : {};
        this.options.overrideSessionInitMessage = this.options.overrideSessionInitMessage || ((sessionInitMessage) => sessionInitMessage);
    }
    initSession(sessionInitOptions) {
        sessionInitOptions.onResponse = sessionInitOptions.onResponse || (() => { });
        // Telemetry used to understand token refresh and WS reconnect patterns in sessions
        const currentTimeMs = Utils.getCurrentTimeMs();
        const timeSinceLastConnectionClose = this.getSessionStats().lastConnectionClose && (currentTimeMs - this.getSessionStats().lastConnectionClose);
        const timeSinceLastSyncMessage = this.getSessionStats().lastSyncMessage && (currentTimeMs - this.getSessionStats().lastSyncMessage);
        const logOp = new LoggingOperation({
            operationName: 'SessionInit',
        }).start();
        const resultDescription = {
            sessionKey: this.sessionKey,
            sessionId: this.clientMetadata.sessionId,
            timeSinceLastConnectionClose,
            timeSinceLastSyncMessage,
            isTokenRefresh: sessionInitOptions.isTokenRefresh,
            error: undefined
        };
        // Initialize the session specifying the protocol, clientMetadata, sessionKey and token
        // SessionKey & origin will be undefined during the first call
        // Token is either auth token provided by the host or anonymous user token generated by the AL service
        const sessionInitMessage = this.options.overrideSessionInitMessage(new SessionInitMessage({
            protocolVersion: currentProtocolVersion,
            clientMetadata: this.clientMetadata,
            sessionKey: this.sessionKey,
            origin: this.origin,
            authToken: this.anonymousToken,
            extensionConfigs: sessionInitOptions.extensionConfigs,
            returnWorkflowInputTypes: true
        }));
        this.sendMessage(sessionInitMessage, (error, response) => {
            logOp.success = !error;
            logOp.resultSignature = this.getSessionStats().lastConnectionClose ? 'Reconnect' : 'FirstConnect';
            logOp.resourceId = response === null || response === void 0 ? void 0 : response.sliceUrl;
            // update sessionKey if different than before
            if ((response === null || response === void 0 ? void 0 : response.sessionKey) && resultDescription.sessionKey !== response.sessionKey) {
                resultDescription.sessionKey = response.sessionKey;
            }
            if (error) {
                resultDescription.error = error.error;
            }
            logOp.resultDescription = JSON.stringify(resultDescription);
            Log.info(0x2224d0d7 /* tag_8jndx */, LogCategory.CoreDefault, logOp.stop());
            sessionInitOptions.onResponse(error, response);
            if (error) {
                return;
            }
            // If we tried to reconnect on same slice and got signal to reconnect on global url we will do so and avoid further processing of current response.
            if (sessionInitOptions.isReconnectOnSameSlice && response.forceReconnect) {
                return;
            }
            // We need both anon token and its expiration time to be valid to proceed
            if (!response.anonymousToken || !response.tokenExpirationSeconds) {
                Log.error(0x2224d0d8 /* tag_8jndy */, LogCategory.CoreDefault, 'AL Anonymous token was not generated for the session');
                return;
            }
            this.anonymousToken = response.anonymousToken;
            this.onSuccessfulSessionInitOnServerSide(response);
        });
    }
    onSuccessfulSessionInitOnServerSide(response) {
        this.tokenRefreshManager.scheduleRefresh(0 /* Anonymous */, response.tokenExpirationSeconds, this.initSession.bind(this, { isTokenRefresh: true }), () => {
            this.anonymousToken = undefined;
        });
        this.initHostAuthToken();
        // Determine which events to emit
        const isReconnectAttempt = !!this.sessionKey;
        const isSeedingRequired = this.sessionKey !== response.sessionKey;
        this.sessionKey = response.sessionKey;
        this.origin = response.origin;
        this.emit('connect', isSeedingRequired, isReconnectAttempt, `${response.sessionUrlBase}/${response.sessionKey}`, response.origin, this.anonymousToken, response.workflowInputTypes);
        if (isReconnectAttempt) {
            this.emit('reconnect');
        }
    }
    initHostAuthToken() {
        if (this.options.getAuthToken) {
            this.options.getAuthToken()
                .then((hostAuthToken) => {
                if (!hostAuthToken) {
                    Log.info(0x2224d0d9 /* tag_8jndz */, LogCategory.CoreDefault, 'Host auth token provision failed');
                    return;
                }
                this.sendTokenProvisionMessage(hostAuthToken);
            })
                .catch(error => {
                Log.error(0x2224d0da /* tag_8jnd0 */, LogCategory.CoreDefault, `Error happened while attempting to fetch host token: ${error}`);
            });
        }
    }
    sendTokenProvisionMessage(hostAuthToken) {
        const tokenProvisionMessage = new TokenProvisionMessage({
            authToken: hostAuthToken
        });
        this.sendMessage(tokenProvisionMessage, this.onTokenProvisionResponse.bind(this), true);
    }
    onTokenProvisionResponse(error, response) {
        if (error || !response || !response.tokenExpirationSeconds) {
            return;
        }
        // Establish token refresh which will be triggered once token is close to expiry
        this.tokenRefreshManager.scheduleRefresh(1 /* Host */, response.tokenExpirationSeconds, this.initHostAuthToken.bind(this));
    }
}
//# sourceMappingURL=session-initializer.js.map